import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import { AuthService, UserService } from '../api';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
  ) {}

  private getApiUrl(endpoint: string): string {
    return environment.apiBaseUrl + endpoint;
  }

  private static isApiRequest(url: string): boolean {
    return url.startsWith('/');
  }

  private refreshTokenAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.authService.user$.pipe(
      switchMap((user) => {
        if (!user) {
          this.router.navigate(['/login']);
          return throwError(() => new Error('User not authenticated'));
        }
        // Get a fresh token
        return from(user.getIdToken(true)).pipe(
          take(1),
          switchMap((newToken) => {
            // Save the new token
            this.userService.setToken(newToken);

            // Clone the request with new token
            const modifiedRequest = request.clone({
              url: this.getApiUrl(request.url),
              setHeaders: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${newToken}`,
              },
            });

            // Retry the request with new token
            return next.handle(modifiedRequest);
          }),
        );
      }),
    );
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (ApiInterceptor.isApiRequest(request.url)) {
      const token = this.userService.getToken();

      // Check if token is available before proceeding
      if (!token) {
        this.router.navigate(['/login']);
        return throwError(() => new Error('No token available'));
      }

      if (this.authService.isTokenExpired(token)) {
        return this.refreshTokenAndRetry(request, next);
      }

      const modifiedRequest = request.clone({
        url: this.getApiUrl(request.url),
        setHeaders: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      // Handle the request directly and only use user$ for error handling
      return next.handle(modifiedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
    } else {
      return next.handle(request);
    }
  }
}
